import type { StateLifecycle } from './stateStyleTypes'

/**
 * Trait & State Type Config - 預設 icon 與顏色
 * 供 trait styles 與 state styles 使用
 */

export interface TypeConfig {
  icon: string
  color: string
  label?: string
}

export type StateDisplayOverrideInput = {
  icon?: string
  label?: string
  unit?: string
}

function toTitleLabelFromStateKey(raw: string): string {
  const key = String(raw || '').trim()
  if (!key) return 'State'
  const token = key.replace(/[_-]+/g, ' ')
  const words = token.split(/\s+/).filter(Boolean)
  return words
    .map((word) => {
      const lower = word.toLowerCase()
      if (lower === 'pm2' || lower === 'pm2.5' || lower === 'pm25') return 'PM2.5'
      if (lower === 'co2') return 'CO2'
      if (lower.length <= 2) return lower.toUpperCase()
      return lower[0].toUpperCase() + lower.slice(1)
    })
    .join(' ')
}

function inferStateConfigByKey(stateKey: string): TypeConfig {
  const key = String(stateKey || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')

  const make = (icon: string, color: string): TypeConfig => ({
    icon,
    color,
    label: toTitleLabelFromStateKey(stateKey),
  })

  if (!key) return make('analytics', '#94a3b8')

  if (key.includes('temperature') || key.includes('temp')) return make('thermostat', '#38bdf8')
  if (key.includes('humidity') || key === 'rh' || key.endsWith('_rh')) return make('humidity_percentage', '#06b6d4')
  if (key.includes('pm2_5') || key.includes('pm25') || key.includes('air_quality')) return make('air', '#94a3b8')
  if (key.includes('co2')) return make('co2', '#64748b')
  if (key.includes('lux') || key.includes('illuminance')) return make('light_mode', '#facc15')
  if (key.includes('watt') || key.includes('apparent_power') || key.includes('real_power') || key.includes('power')) {
    return make('electric_bolt', '#f59e0b')
  }
  if (key.includes('voltage')) return make('electrical_services', '#94a3b8')
  if (key.includes('current')) return make('power', '#94a3b8')
  if (key.includes('battery')) return make('battery_horiz_075', '#22c55e')
  if (key.includes('brightness')) return make('lightbulb', '#facc15')
  if (key.includes('fan') || key.includes('airflow')) return make('air', '#38bdf8')
  if (key.includes('color')) return make('palette', '#a855f7')
  if (key.includes('mode') || key.includes('setting') || key.includes('channel')) return make('tune', '#38bdf8')
  if (key.includes('onoff') || key === 'on' || key === 'off' || key.includes('toggle')) return make('power', '#22c55e')
  if (key.includes('occupancy') || key.includes('presence')) return make('event_seat', '#6366f1')
  if (key.includes('motion')) return make('motion_sensor_idle', '#3b82f6')
  if (key.includes('contact') || key.includes('door')) return make('sensor_door', '#8b5cf6')

  return make('analytics', '#94a3b8')
}

/** Trait 預設 icon & 顏色 */
export const TRAIT_TYPE_CONFIG: Record<string, TypeConfig> = {
  Brightness: { icon: 'lightbulb', color: '#facc15' },
  HumiditySetting: { icon: 'water_drop', color: '#06b6d4' },
  OpenClose: { icon: 'blinds', color: '#94a3b8' },
  TemperatureSetting: { icon: 'thermostat', color: '#38bdf8' },
  TemperatureControl: { icon: 'thermostat', color: '#38bdf8' },
  OnOff: { icon: 'power', color: '#22c55e' },
  StartStop: { icon: 'power_settings_new', color: '#22c55e' },
  OnOffToggle: { icon: 'power', color: '#22c55e' },
  FanSpeed: { icon: 'air', color: '#38bdf8' },
  CustomModes: { icon: 'tune', color: '#38bdf8' },
  Modes: { icon: 'tune', color: '#38bdf8' },
  CustomToggles: { icon: 'toggle_on', color: '#facc15' },
  ThermostatSetMode: { icon: 'ac_unit', color: '#38bdf8' },
  ColorSetting: { icon: 'palette', color: '#a855f7' },
  ColorTemperature: { icon: 'wb_sunny', color: '#facc15' },
  Occupancy: { icon: 'event_seat', color: '#6366f1' },
  Detector: { icon: 'motion_sensor_idle', color: '#3b82f6' },
  ZoneAlarm: { icon: 'security', color: '#ef4444' },
  Coordinator: { icon: 'hub', color: '#38bdf8' },
  SmartConnect: { icon: 'wifi', color: '#22c55e' },
  Scene: { icon: 'scene', color: '#8b5cf6' },
}

/** State 預設 icon & 顏色（對應感測/狀態 key） */
export const STATE_TYPE_CONFIG: Record<string, TypeConfig> = {
  // 感測
  temperature: { icon: 'thermostat', color: '#38bdf8', label: 'Temperature' },
  humidity: { icon: 'humidity_percentage', color: '#06b6d4', label: 'Humidity' },
  rh: { icon: 'humidity_percentage', color: '#06b6d4', label: 'Humidity' },
  pm2_5: { icon: 'air', color: '#94a3b8', label: 'PM2.5' },
  co2: { icon: 'co2', color: '#64748b', label: 'CO₂' },
  lux: { icon: 'light_mode', color: '#facc15', label: 'Illuminance' },
  illuminance: { icon: 'light_mode', color: '#facc15', label: 'Illuminance' },
  watt: { icon: 'electric_bolt', color: '#f59e0b', label: 'Power' },
  watt_hour: { icon: 'bolt', color: '#f59e0b', label: 'Energy' },
  real_power: { icon: 'electric_bolt', color: '#f59e0b', label: 'Power' },
  voltage: { icon: 'electrical_services', color: '#94a3b8', label: 'Voltage' },
  current: { icon: 'power', color: '#94a3b8', label: 'Current' },
  battery: { icon: 'battery_horiz_075', color: '#22c55e', label: 'Battery' },
  on: { icon: 'power', color: '#22c55e', label: 'OnOff' },
  brightness: { icon: 'lightbulb', color: '#facc15', label: 'Brightness' },
  fan_speed: { icon: 'air', color: '#38bdf8', label: 'FanSpeed' },
  thermostatTemperatureSetpoint: { icon: 'thermostat', color: '#38bdf8', label: 'Target temp' },
  openClosePercent: { icon: 'blinds', color: '#94a3b8', label: 'OpenClose' },
  occupancy: { icon: 'event_seat', color: '#6366f1', label: 'Occupancy' },
  motion: { icon: 'motion_sensor_idle', color: '#3b82f6', label: 'Motion' },
  contact: { icon: 'sensor_door', color: '#8b5cf6', label: 'Contact' },
}

/** State 預設單位 */
export const STATE_UNIT: Record<string, string> = {
  temperature: '°C',
  humidity: '%',
  rh: '%',
  brightness: '%',
  on: '',
  fan_speed: '',
  watt: ' W',
  watt_hour: ' kWh',
  lux: ' lux',
  pm2_5: ' μg/m³',
  co2: ' ppm',
  battery: '%',
}

export const STATE_LIFECYCLE_COLORS: Record<StateLifecycle, string> = {
  live: '#22c55e',
  warn: '#f59e0b',
  error: '#ef4444',
  stale: '#94a3b8',
  offline: '#64748b',
}

/** 取得 trait 的預設 config */
export function getTraitConfig(trait: string): TypeConfig {
  const key = String(trait || '').trim()
  return TRAIT_TYPE_CONFIG[key] ?? { icon: 'tune', color: '#38bdf8' }
}

/** Device type → Material icon (for OnOff vertical slider, etc.) */
const DEVICE_TYPE_ICONS: Record<string, string> = {
  light: 'lightbulb',
  hvac: 'ac_unit',
  ac_unit: 'ac_unit',
  fan: 'mode_fan',
  switch: 'toggle_on',
  outlet: 'outlet',
  th_sensor: 'device_thermostat',
  presence_sensor: 'motion_sensor_active',
  lux_sensor: 'light_mode',
  multi_sensor: 'sensors',
  power_meter: 'electric_meter',
  power_meter_3phase: 'bolt',
  electrical_sensor_1p3w: 'electric_meter',
  electrical_sensor_3p4w: 'bolt',
  door_sensor: 'sensor_door',
  button: 'touch_app',
  dehumidifier: 'humidity_mid',
  erv: 'air',
  curtain: 'blinds',
  blind: 'blinds',
  generic: 'devices',
}

/** 取得 device type 對應的 icon，預設 Power State (power_settings_new) */
export function getDeviceTypeIcon(device?: { type?: string } | null): string {
  const type = (device?.type ?? '').toLowerCase().trim()
  if (type && DEVICE_TYPE_ICONS[type]) return DEVICE_TYPE_ICONS[type]
  return 'power_settings_new'
}

/** 取得 state 的預設 config（支援 snake_case 與 camelCase） */
export function getStateConfig(stateKey: string, override?: StateDisplayOverrideInput): TypeConfig {
  const key = String(stateKey || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '')
  const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
  const base =
    STATE_TYPE_CONFIG[stateKey] ??
    STATE_TYPE_CONFIG[key] ??
    STATE_TYPE_CONFIG[camel] ??
    inferStateConfigByKey(stateKey)
  const icon = String(override?.icon || '').trim()
  const label = String(override?.label || '').trim()
  return {
    ...base,
    icon: icon || base.icon,
    label: label || base.label,
  }
}

/** 取得 state 的預設單位 */
export function getStateUnit(stateKey: string, override?: StateDisplayOverrideInput): string {
  const overrideUnit = String(override?.unit || '')
  if (overrideUnit) return overrideUnit
  const k = String(stateKey || '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '')
  return STATE_UNIT[k] ?? STATE_UNIT[stateKey] ?? ''
}

export function normalizeStateLifecycle(value: unknown): StateLifecycle {
  const token = String(value ?? '')
    .trim()
    .toLowerCase()
  if (token === 'warn' || token === 'warning') return 'warn'
  if (token === 'error' || token === 'critical') return 'error'
  if (token === 'stale') return 'stale'
  if (token === 'offline') return 'offline'
  return 'live'
}

export function getStateLifecycleColor(lifecycle: StateLifecycle): string {
  return STATE_LIFECYCLE_COLORS[normalizeStateLifecycle(lifecycle)] ?? STATE_LIFECYCLE_COLORS.live
}

export function inferStateLifecycle(
  stateKey: string,
  value: unknown,
  options?: { thresholdEnabled?: boolean; deviceOnline?: boolean | null }
): StateLifecycle {
  if (options?.deviceOnline === false) return 'offline'
  if (value == null || value === '—') return 'stale'

  const k = String(stateKey || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')

  if (!options?.thresholdEnabled) return 'live'

  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return 'live'

  if (k === 'pm2_5') {
    if (n >= 55) return 'error'
    if (n >= 35) return 'warn'
    return 'live'
  }
  if (k === 'co2') {
    if (n >= 1500) return 'error'
    if (n >= 1000) return 'warn'
    return 'live'
  }
  if (k === 'temperature') {
    if (n >= 36 || n <= 5) return 'error'
    if (n >= 30 || n <= 10) return 'warn'
    return 'live'
  }
  if (k === 'humidity' || k === 'rh') {
    if (n >= 85 || n <= 10) return 'error'
    if (n >= 75 || n <= 20) return 'warn'
    return 'live'
  }
  if (k === 'battery') {
    if (n <= 5) return 'error'
    if (n <= 15) return 'warn'
    return 'live'
  }
  return 'live'
}

export function formatStateUpdatedAtLabel(minutesAgo: number): string {
  const safe = Number.isFinite(minutesAgo) ? Math.max(0, Math.round(minutesAgo)) : 0
  if (safe <= 0) return '剛剛更新'
  return `${safe}m 前更新`
}

/**
 * Get mock attributes for trait (preview when no deviceSN).
 * Aligned with trait-commands.json / API attribute structure.
 */
export function getMockAttributesForTrait(trait: string): Record<string, unknown> {
  const t = String(trait || '').trim()
  const defaults: Record<string, Record<string, unknown>> = {
    Brightness: { brightnessRange: { min: 0, max: 100 } },
    ColorSetting: {
      colorTemperatureRange: {
        temperatureMinK: 2700,
        temperatureMaxK: 6500,
      },
    },
    ColorTemperature: {
      colorTemperatureRange: {
        temperatureMinK: 2700,
        temperatureMaxK: 6500,
      },
    },
    TemperatureSetting: {
      thermostatTemperatureRange: { minThresholdCelsius: DEFAULT_TEMPERATURE_RANGE.min, maxThresholdCelsius: DEFAULT_TEMPERATURE_RANGE.max },
      availableThermostatModes: ['cool', 'heat', 'dry', 'fan-only', 'auto'],
    },
    TemperatureControl: {
      temperatureRange: { minThresholdCelsius: DEFAULT_TEMPERATURE_RANGE.min, maxThresholdCelsius: DEFAULT_TEMPERATURE_RANGE.max },
    },
    HumiditySetting: { humiditySetpointRange: { minPercent: 30, maxPercent: 80 } },
    OpenClose: {},
    FanSpeed: {
      availableFanSpeeds: [
        { speed_name: 'low', speed_values: ['low'] },
        { speed_name: 'medium', speed_values: ['medium'] },
        { speed_name: 'high', speed_values: ['high'] },
        { speed_name: 'auto', speed_values: ['auto'] },
      ],
    },
    ThermostatSetMode: {
      availableThermostatModes: ['cool', 'heat', 'dry', 'fan-only', 'auto'],
    },
    CustomModes: {
      availableCustomModes: [
        { name: 'mode1', settings: ['a', 'b'], ordered: true },
        { name: 'mode2', settings: ['x', 'y'], ordered: true },
        { name: 'mode3', settings: ['1', '2'], ordered: true },
      ],
    },
    CustomToggles: {
      availableCustomToggles: ['eco', 'sleep', 'turbo'],
    },
    Modes: {
      availableModes: [
        { name: 'operation_mode', settings: ['cool', 'heat', 'auto'], ordered: true },
      ],
    },
  }
  return defaults[t] ?? {}
}

/** 色溫預設範圍 (K)，無 attributes 時使用 */
export const DEFAULT_COLOR_TEMP_RANGE = { min: 2700, max: 6500 }

/** 溫度預設範圍 (°C)，無 attributes 時使用；正式環境以 attributes 為準 */
export const DEFAULT_TEMPERATURE_RANGE = { min: 16, max: 32 }

/** Resolve min/max from attributes (handles API formats: temperatureMinK, minThresholdCelsius, minPercent, min) */
export function resolveRangeFromAttributes(
  attrs: Record<string, unknown> | undefined,
  trait: string,
  rangeKey: string
): { min: number; max: number } {
  const fallbacks: Record<string, { min: number; max: number }> = {
    brightness: { min: 0, max: 100 },
    colorTemperature: DEFAULT_COLOR_TEMP_RANGE,
    temperature: DEFAULT_TEMPERATURE_RANGE,
    humidity: { min: 30, max: 80 },
    openClose: { min: 0, max: 100 },
  }
  const fallback = fallbacks[rangeKey] ?? { min: 0, max: 100 }

  if (!attrs) return fallback

  const ct = attrs.colorTemperatureRange as Record<string, number> | undefined
  if (ct && (rangeKey === 'colorTemperature' || trait === 'ColorSetting' || trait === 'ColorTemperature')) {
    const min = ct.temperatureMinK ?? ct.min ?? DEFAULT_COLOR_TEMP_RANGE.min
    const max = ct.temperatureMaxK ?? ct.max ?? DEFAULT_COLOR_TEMP_RANGE.max
    return { min: Number(min), max: Number(max) }
  }

  const tt = attrs.thermostatTemperatureRange as Record<string, number> | undefined
  if (tt && (rangeKey === 'temperature' || trait.includes('Temperature'))) {
    const min = tt.minThresholdCelsius ?? tt.min ?? fallback.min
    const max = tt.maxThresholdCelsius ?? tt.max ?? fallback.max
    return { min: Number(min), max: Number(max) }
  }

  const tr = attrs.temperatureRange as Record<string, number> | undefined
  if (tr && rangeKey === 'temperature') {
    const min = tr.minThresholdCelsius ?? tr.min ?? fallback.min
    const max = tr.maxThresholdCelsius ?? tr.max ?? fallback.max
    return { min: Number(min), max: Number(max) }
  }

  const hr = attrs.humiditySetpointRange as Record<string, number> | undefined
  if (hr && rangeKey === 'humidity') {
    const min = hr.minPercent ?? hr.min ?? fallback.min
    const max = hr.maxPercent ?? hr.max ?? fallback.max
    return { min: Number(min), max: Number(max) }
  }

  const br = attrs.brightnessRange as Record<string, number> | undefined
  if (br && rangeKey === 'brightness') {
    return { min: Number(br.min ?? 0), max: Number(br.max ?? 100) }
  }

  return fallback
}

/** Resolve modes array from attributes */
export function resolveModesFromAttributes(
  attrs: Record<string, unknown> | undefined,
  trait: string
): string[] {
  if (!attrs) {
    if (trait === 'ThermostatSetMode') return ['cool', 'heat', 'dry', 'fan-only', 'auto']
    if (trait === 'FanSpeed') return ['low', 'medium', 'high', 'auto']
    if (trait === 'Modes') return ['mode1', 'mode2', 'mode3']
    return []
  }
  const modes = attrs.availableThermostatModes
  if (Array.isArray(modes) && modes.length) {
    return modes
      .map((mode) => String(mode ?? '').trim().toLowerCase())
      .filter(Boolean)
  }
  if (typeof modes === 'string') {
    const parsed = modes
      .split(',')
      .map((mode) => mode.trim().toLowerCase())
      .filter(Boolean)
    if (parsed.length) return parsed
  }
  const fanSpeeds = attrs.availableFanSpeeds as Array<{ speed_name?: string }> | undefined
  if (Array.isArray(fanSpeeds) && fanSpeeds.length) {
    return fanSpeeds.map((s) => String(s.speed_name ?? s).toLowerCase())
  }
  const availModes = attrs.availableModes as Array<{ name?: string; settings?: string[] }> | undefined
  if (Array.isArray(availModes) && availModes.length) {
    const settings = availModes[0]?.settings
    if (Array.isArray(settings) && settings.length) return settings
  }
  if (trait === 'FanSpeed') return ['low', 'medium', 'high', 'auto']
  if (trait === 'ThermostatSetMode') return ['cool', 'heat', 'dry', 'fan-only', 'auto']
  if (trait === 'Modes') return ['mode1', 'mode2', 'mode3']
  return []
}

/** Default display labels for FanSpeed (LOW, MED, HIGH, AUTO) */
export const FAN_SPEED_DISPLAY_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Med',
  med: 'Med',
  high: 'High',
  turbo: 'Turbo',
  auto: 'Auto',
}

/**
 * Resolve FanSpeed display labels from customControls (SettingSynonym).
 * customControls structure: { [endpoint].Modes.FanSpeed.{lang}.SettingSynonym: { [speedId]: ["Label"] } }
 * @param customControls - From device.customControls (group level)
 * @param endpoint - From deviceSN (part after #) or capability endpoint
 * @param speedIds - Resolved speed IDs (e.g. ['low','medium','high','auto'])
 * @param lang - Preferred locale: 'zh-TW' | 'en'
 */
export function resolveFanSpeedLabelsFromCustomControls(
  customControls: Record<string, unknown> | undefined,
  endpoint: string | undefined,
  speedIds: string[],
  lang: 'zh-TW' | 'en' = 'en'
): Record<string, string> {
  const result: Record<string, string> = {}
  if (!customControls || !speedIds.length) return result

  const epKey = endpoint || '1'
  const epData = (customControls[epKey] ?? customControls) as Record<string, unknown> | undefined
  const modes = (epData?.Modes ?? epData?.modes) as Record<string, unknown> | undefined
  const fanSpeedMode = (modes?.FanSpeed ?? modes?.fanSpeed) as Record<string, unknown> | undefined
  const settingMaps = collectSettingSynonymMaps(fanSpeedMode, lang)
  if (settingMaps.length === 0) return result

  for (const id of speedIds) {
    const key = id.toLowerCase()
    for (const settingSynonym of settingMaps) {
      const raw = settingSynonym[key] ?? settingSynonym[id]
      if (raw == null) continue
      const label = Array.isArray(raw) ? raw[0] : String(raw)
      if (label) {
        result[key] = String(label).trim()
        break
      }
    }
  }
  return result
}

/** Resolve custom toggles from attributes */
export function resolveCustomTogglesFromAttributes(attrs: Record<string, unknown> | undefined): string[] {
  if (!attrs) return ['eco', 'sleep', 'turbo']
  const toggles = attrs.availableCustomToggles as string[] | undefined
  return Array.isArray(toggles) && toggles.length ? toggles : ['eco', 'sleep', 'turbo']
}

export type CustomModeResolvedOption = {
  id: string
  modeValue: string
  label: string
}

export type CustomModeResolvedConfig = {
  modeName: string
  options: CustomModeResolvedOption[]
}

export function resolveCustomModeIcon(modeName: string | undefined, fallbackIcon = 'tune'): string {
  const normalized = String(modeName || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
  if (!normalized) return fallbackIcon
  if (normalized.includes('fanspeed') || (normalized.includes('fan') && normalized.includes('speed'))) {
    return 'mode_fan'
  }
  if (normalized.includes('thermostat') || normalized.includes('temperature')) return 'thermostat'
  if (normalized.includes('humidity')) return 'humidity_percentage'
  if (normalized.includes('mode')) return 'tune'
  return fallbackIcon
}

function findCaseInsensitiveKey(
  source: Record<string, unknown> | undefined,
  target: string
): string | null {
  if (!source || !target) return null
  const normalized = String(target || '').trim().toLowerCase()
  if (!normalized) return null
  for (const key of Object.keys(source)) {
    if (String(key || '').trim().toLowerCase() === normalized) return key
  }
  return null
}

function toNonEmptyStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => String(item ?? '').trim())
    .filter(Boolean)
}

function collectSettingSynonymMaps(
  modeData: Record<string, unknown> | undefined,
  lang: 'zh-TW' | 'en'
): Array<Record<string, unknown>> {
  if (!modeData || typeof modeData !== 'object') return []
  const localeOrder = Array.from(new Set([lang, 'en', 'zh-TW', 'zh']))
  const maps: Array<Record<string, unknown>> = []
  for (const locale of localeOrder) {
    const localeData = modeData[locale] as Record<string, unknown> | undefined
    const settingSynonym = localeData?.SettingSynonym as Record<string, unknown> | undefined
    if (settingSynonym && typeof settingSynonym === 'object') maps.push(settingSynonym)
  }
  return maps
}

function resolveCustomModeLabelMap(
  customControls: Record<string, unknown> | undefined,
  endpoint: string | undefined,
  modeName: string,
  lang: 'zh-TW' | 'en'
): Record<string, string> {
  const result: Record<string, string> = {}
  if (!customControls || !modeName) return result

  const epKey = String(endpoint || '').trim() || '1'
  const epData = (customControls[epKey] ?? customControls) as Record<string, unknown> | undefined
  const modes = (epData?.Modes ?? epData?.modes) as Record<string, unknown> | undefined
  if (!modes || typeof modes !== 'object') return result

  const modeKey = findCaseInsensitiveKey(modes, modeName)
  if (!modeKey) return result
  const modeData = modes[modeKey] as Record<string, unknown> | undefined
  if (!modeData || typeof modeData !== 'object') return result

  const settingMaps = collectSettingSynonymMaps(modeData, lang)
  if (settingMaps.length === 0) return result

  // Merge locales by priority and fill missing keys from fallback locales.
  for (const settingSynonym of settingMaps) {
    for (const [rawKey, rawValue] of Object.entries(settingSynonym)) {
      const modeValue = String(rawKey || '').trim()
      if (!modeValue || result[modeValue]) continue
      const label = Array.isArray(rawValue) ? String(rawValue[0] ?? '').trim() : String(rawValue ?? '').trim()
      if (!label) continue
      result[modeValue] = label
    }
  }
  return result
}

export function resolveCustomModesFromMcp(
  attrs: Record<string, unknown> | undefined,
  customControls: Record<string, unknown> | undefined,
  endpoint: string | undefined,
  lang: 'zh-TW' | 'en' = 'en',
  _deviceTraits?: string[]
): CustomModeResolvedConfig {
  const fallback: CustomModeResolvedConfig = {
    modeName: 'Mode',
    options: [
      { id: 'Mode:mode1', modeValue: 'mode1', label: 'Mode 1' },
      { id: 'Mode:mode2', modeValue: 'mode2', label: 'Mode 2' },
      { id: 'Mode:mode3', modeValue: 'mode3', label: 'Mode 3' },
    ],
  }

  const rawModes = attrs?.availableCustomModes as Array<{ name?: unknown; settings?: unknown }> | undefined
  if (!Array.isArray(rawModes) || rawModes.length === 0) return fallback

  const normalizedModes = rawModes
    .map((mode) => {
      const modeName = String(mode?.name ?? '').trim()
      const settings = toNonEmptyStringArray(mode?.settings)
      return { modeName, settings }
    })
    .filter((mode) => mode.modeName || mode.settings.length > 0)
  if (normalizedModes.length === 0) return fallback

  const looksLikeFanSpeed = (modeName: string): boolean => {
    const normalized = String(modeName || '').trim().toLowerCase()
    return normalized === 'fanspeed' || normalized === 'fan_speed' || normalized === 'fan speed'
  }
  const looksLikeGeneralMode = (modeName: string): boolean => {
    const normalized = String(modeName || '').trim().toLowerCase()
    return normalized === 'mode' || normalized.endsWith('mode') || normalized.includes(' mode ')
  }

  const nonFanSpeedCandidate = normalizedModes.find(
    (mode) => !looksLikeFanSpeed(mode.modeName) && mode.settings.length > 0
  )

  const preferred = normalizedModes.find((mode) => mode.modeName.toLowerCase() === 'mode')
    ?? normalizedModes.find((mode) => looksLikeGeneralMode(mode.modeName))
    ?? nonFanSpeedCandidate
    ?? normalizedModes.find((mode) => mode.settings.length > 0)
    ?? normalizedModes[0]
  const modeName = preferred.modeName || 'Mode'
  const labelMap = resolveCustomModeLabelMap(customControls, endpoint, modeName, lang)

  if (preferred.settings.length > 0) {
    const options = preferred.settings.map((modeValue) => ({
      id: `${modeName}:${modeValue}`,
      modeValue,
      label: labelMap[modeValue] ?? modeValue,
    }))
    return { modeName, options }
  }

  const fallbackByName = normalizedModes.map((mode, index) => {
    const modeValue = mode.modeName || `mode${index + 1}`
    return {
      id: `${modeName}:${modeValue}`,
      modeValue,
      label: labelMap[modeValue] ?? modeValue,
    }
  })
  return { modeName, options: fallbackByName.length ? fallbackByName : fallback.options }
}

function resolveStringValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed || null
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'object' && value) {
    const objectValue = value as Record<string, unknown>
    const nested =
      resolveStringValue(objectValue.value) ??
      resolveStringValue(objectValue.mode) ??
      resolveStringValue(objectValue.setting)
    if (nested) return nested
  }
  return null
}

export function resolveCurrentCustomModeValue(
  state: Record<string, unknown> | undefined,
  modeName: string,
  fallbackValue: string
): string {
  if (!state || typeof state !== 'object') return fallbackValue
  const normalizedModeName = String(modeName || '').trim()
  const modeLower = normalizedModeName.toLowerCase()

  const direct =
    resolveStringValue(state[normalizedModeName]) ??
    resolveStringValue(state[modeLower]) ??
    resolveStringValue(state.currentMode) ??
    resolveStringValue(state.currentCustomMode) ??
    resolveStringValue(state.mode) ??
    resolveStringValue(state.customMode)
  if (direct) return direct

  const modeSettings = state.modeSettings as Record<string, unknown> | undefined
  const updateModeSettings = state.updateModeSettings as Record<string, unknown> | undefined
  const nested = (container: Record<string, unknown> | undefined): string | null => {
    if (!container || typeof container !== 'object') return null
    const exact = resolveStringValue(container[normalizedModeName])
    if (exact) return exact
    const key = findCaseInsensitiveKey(container, normalizedModeName)
    if (!key) return null
    return resolveStringValue(container[key])
  }
  return nested(modeSettings) ?? nested(updateModeSettings) ?? fallbackValue
}

/** Resolve custom modes from attributes */
export function resolveCustomModesFromAttributes(attrs: Record<string, unknown> | undefined): Array<{ id: string; name: string }> {
  if (!attrs) return [{ id: 'mode1', name: 'Mode 1' }, { id: 'mode2', name: 'Mode 2' }, { id: 'mode3', name: 'Mode 3' }]
  const modes = attrs.availableCustomModes as Array<{ name?: string; settings?: unknown[] }> | undefined
  if (Array.isArray(modes) && modes.length) {
    return modes.map((m, i) => ({ id: String(m.name ?? `mode${i + 1}`), name: String(m.name ?? `Mode ${i + 1}`) }))
  }
  return [{ id: 'mode1', name: 'Mode 1' }, { id: 'mode2', name: 'Mode 2' }, { id: 'mode3', name: 'Mode 3' }]
}

/** 將 state value 轉為可顯示字串，避免 object 顯示為 [object Object] */
export function formatStateValue(value: unknown, precision?: number): string {
  const safePrecision =
    typeof precision === 'number' && Number.isFinite(precision) && precision >= 0
      ? Math.min(4, Math.max(0, Math.round(precision)))
      : null
  if (value == null) return '—'
  if (typeof value === 'boolean') return value ? 'On' : 'Off'
  if (typeof value === 'number') {
    if (safePrecision != null) {
      return value.toFixed(safePrecision)
    }
    return String(value)
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>
    if (o.mode != null && typeof o.mode === 'string') return o.mode
    if (typeof o.value === 'number') {
      if (safePrecision != null) {
        return o.value.toFixed(safePrecision)
      }
      return String(o.value)
    }
    if (typeof o.value === 'string') return String(o.value)
    return JSON.stringify(value)
  }
  return String(value)
}

/** 取得 state 的預設 mock 值（用於預覽） */
export function getStateMockValue(stateKey: string): unknown {
  const k = String(stateKey || '').trim().toLowerCase()
  const m: Record<string, unknown> = {
    temperature: 25,
    humidity: 60,
    rh: 60,
    brightness: 80,
    on: true,
    fan_speed: 'medium',
    watt: 120,
    watt_hour: 2.5,
    lux: 350,
    pm2_5: 12,
    co2: 450,
    battery: 85,
    occupancy: true,
    motion: false,
    contact: false,
  }
  return m[k] ?? m[stateKey] ?? '—'
}
